import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Button, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsRefinance: React.FC = () => {
  const [loanBalance, setLoanBalance] = useState(400000);
  const [currentRate, setCurrentRate] = useState(6.5);
  const [newRate, setNewRate] = useState(5.9);
  const [loanTerm, setLoanTerm] = useState(25);

  const calculateRepayment = (principal: number, rate: number, years: number) => {
    const monthlyRate = rate / 100 / 12;
    const months = years * 12;
    return (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
  };

  const currentRepayment = calculateRepayment(loanBalance, currentRate, loanTerm);
  const newRepayment = calculateRepayment(loanBalance, newRate, loanTerm);
  const monthlySaving = currentRepayment - newRepayment;
  const yearlySaving = monthlySaving * 12;

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/tools/refinance" fallbackTitle="Refinancing Calculator" fallbackDescription="Calculate refinancing benefits and savings" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">Refinance Calculator</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Calculate your refinance savings</Text>
          </VStack>
        </Container>
      </Box>
      <Container maxW="container.xl" py={20} flex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg">
            <Heading size="md" mb={6}>Loan Details</Heading>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Current Loan Balance</FormLabel>
                <Input type="number" value={loanBalance} onChange={(e) => setLoanBalance(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>${loanBalance.toLocaleString()}</Text>
              </FormControl>
              <FormControl>
                <FormLabel>Current Interest Rate: {currentRate}%</FormLabel>
                <Slider value={currentRate} onChange={setCurrentRate} min={3} max={10} step={0.1}>
                  <SliderTrack><SliderFilledTrack bg="red.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>
              <FormControl>
                <FormLabel>New Interest Rate: {newRate}%</FormLabel>
                <Slider value={newRate} onChange={setNewRate} min={3} max={10} step={0.1}>
                  <SliderTrack><SliderFilledTrack bg="green.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>
              <FormControl>
                <FormLabel>Remaining Loan Term: {loanTerm} years</FormLabel>
                <Slider value={loanTerm} onChange={setLoanTerm} min={5} max={30} step={1}>
                  <SliderTrack><SliderFilledTrack bg="blue.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>
              <Button colorScheme="green" size="lg">Calculate Savings</Button>
            </VStack>
          </Box>
          <Box>
            <Box bg="gradient-to-br" bgGradient="linear(to-br, green.500, blue.600)" p={8} borderRadius="xl" color="white" textAlign="center" mb={6}>
              <Text fontSize="sm" textTransform="uppercase" mb={2}>Your Potential Monthly Saving</Text>
              <Heading size="3xl" mb={2}>${Math.round(monthlySaving).toLocaleString()}</Heading>
              <Text fontSize="sm">That's ${Math.round(yearlySaving).toLocaleString()} per year!</Text>
            </Box>
            <SimpleGrid columns={2} spacing={4} mb={6}>
              <Stat bg="red.50" p={4} borderRadius="lg">
                <StatLabel>Current Repayment</StatLabel>
                <StatNumber fontSize="xl">${Math.round(currentRepayment).toLocaleString()}</StatNumber>
                <StatHelpText>at {currentRate}%</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg">
                <StatLabel>New Repayment</StatLabel>
                <StatNumber fontSize="xl">${Math.round(newRepayment).toLocaleString()}</StatNumber>
                <StatHelpText>at {newRate}%</StatHelpText>
              </Stat>
            </SimpleGrid>
            <Box bg="blue.50" p={6} borderRadius="lg">
              <Heading size="sm" mb={3}>Refinancing Benefits</Heading>
              <VStack align="start" spacing={2} fontSize="sm">
                <Text>✓ Potential savings: ${Math.round(yearlySaving * loanTerm).toLocaleString()} over loan term</Text>
                <Text>✓ Access better loan features</Text>
                <Text>✓ Consolidate debts</Text>
                <Text>✓ Access equity for investment</Text>
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsRefinance;
